/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/shared/validateA2Form.ts.
 */

import type {
  FormDataResponse,
  FormField,
  FormSubSection,
} from "../../../../../interfaces/formInterfaces";
import type { FuelDensityApiRow } from "../../../../../services/a1Service";
import type { CalorificValuePublic } from "../../../../../services/calorificValuesPublicService";
import { A2_MAX_FUELS_PER_RAW_MATERIAL } from "../A2/utils/a2FuelLayout";
import { A2_MAX_VEHICLES_PER_RAW_MATERIAL } from "../A2/utils/a2VehicleLayout";
import {
  buildSectionFieldKey,
  type ContributorFormErrors,
  fieldLabel,
  getFirstFormError,
  hasContributorUnitFieldValue,
  hasFieldValue,
} from "./contributorFormValidation";
import { validateContributorSectionRows } from "./validateContributorSectionRows";
import {
  A2_FUEL_MIX_FIELD,
  A2_FUEL_SECTION_CODE,
  shouldSkipCalorificValueMandatoryValidation,
  shouldSkipFuelDensityMandatoryValidation,
} from "./fuelMixDensityValidation";

export { getFirstFormError };

const A2_VEHICLE_CODE = "A2_VEHICLE_DETAILS";
/** CMS section; embodied energy fields render under each A2_FUEL_TRANSPORT row. */
const A2_EMBODIED_ENERGY_CODE = "A2_EMBODIED_ENERGY";

const A2_EMBODIED_ENERGY_FIELD_NAMES = new Set([
  "embodiedEnergy",
  "embodiedEnergy_unit",
]);

/** Legacy CMS fields — not rendered in Form A2 UI; excluded from validation. */
const A2_NON_UI_FIELD_NAMES = new Set([
  "unitEmbodiedCarbonFunctional",
  "UnitEmbodiedCarbonFunctionalUnit",
]);

/** Skip UNIT validation when the paired numeric/value field is empty. */
const A2_UNIT_PAIRED_VALUE_FIELD: Record<string, string> = {
  fuelQuantity_unit: "fuelQuantity",
  fuelDensity_unit: "fuelDensity",
  calorificValue_unit: "calorificValue",
  embodiedEnergy_unit: "embodiedEnergy",
  characterizationFactor_unit: "characterizationFactor",
  embodiedCarbon_referenceQty_unit: "embodiedCarbon_referenceQty",
  embodiedCarbon_functionalUnit_unit: "embodiedCarbon_functionalUnit",
};

function isA2FuelRowActive(
  values: Record<string, string>,
  rowIndex: number
): boolean {
  const mixKey = buildSectionFieldKey(
    A2_FUEL_MIX_FIELD,
    A2_FUEL_SECTION_CODE,
    rowIndex
  );
  return hasFieldValue(values[mixKey]);
}

function shouldSkipA2UnitField(
  field: FormField,
  sectionCode: string,
  rowIndex: number,
  values: Record<string, string>
): boolean {
  if (field.type !== "UNIT" && !field.name.endsWith("_unit")) return false;

  const paired = A2_UNIT_PAIRED_VALUE_FIELD[field.name];
  if (!paired) return false;

  const valueKey = buildSectionFieldKey(paired, sectionCode, rowIndex);
  return !hasFieldValue(values[valueKey]);
}

function fieldValuePresent(
  field: FormField,
  values: Record<string, string>,
  key: string
): boolean {
  const raw = values[key];
  if (field.type === "UNIT" || field.name.endsWith("_unit")) {
    return hasContributorUnitFieldValue(raw);
  }
  return hasFieldValue(raw);
}

const A2_FORCE_EDITABLE = new Set([
  "characterizationFactor_unit",
  "embodiedCarbon_referenceQty_unit",
  "embodiedCarbon_functionalUnit_unit",
]);

function isA2FieldDisabled(field: FormField): boolean {
  return (
    (field.type === "READONLY" ||
      field.isEditable === false ||
      field.name === "state" ||
      field.name === "district") &&
    !A2_FORCE_EDITABLE.has(field.name)
  );
}

function vehicleCounts(
  rawMaterialsLength: number,
  vehiclesPerRawMaterial: number[]
): number[] {
  if (rawMaterialsLength === 0) return [];
  return vehiclesPerRawMaterial.length >= rawMaterialsLength
    ? vehiclesPerRawMaterial
        .slice(0, rawMaterialsLength)
        .map((c) =>
          Math.min(A2_MAX_VEHICLES_PER_RAW_MATERIAL, Math.max(1, c))
        )
    : Array.from({ length: rawMaterialsLength }, () => 1);
}

function fuelCounts(
  rawMaterialsLength: number,
  fuelsPerRawMaterial: number[]
): number[] {
  if (rawMaterialsLength === 0) return [];
  return fuelsPerRawMaterial.length >= rawMaterialsLength
    ? fuelsPerRawMaterial
        .slice(0, rawMaterialsLength)
        .map((c) => Math.min(A2_MAX_FUELS_PER_RAW_MATERIAL, Math.max(1, c)))
    : Array.from({ length: rawMaterialsLength }, () => 1);
}

function totalFlatRows(counts: number[]): number {
  return counts.reduce((sum, c) => sum + Math.max(1, c), 0);
}

function materialIndexForFlatRow(
  flatRowIndex: number,
  counts: number[]
): { materialIndex: number; vehiclesForMaterial: number } | null {
  let flat = 0;
  for (let m = 0; m < counts.length; m++) {
    const n = Math.max(1, counts[m] ?? 1);
    if (flatRowIndex >= flat && flatRowIndex < flat + n) {
      return { materialIndex: m, vehiclesForMaterial: n };
    }
    flat += n;
  }
  return null;
}

function isA2TripsCalculated(
  fieldName: string,
  sectionCode: string,
  rowIndex: number,
  rawMaterialsLength: number,
  vehiclesPerRawMaterial: number[]
): boolean {
  if (sectionCode !== A2_VEHICLE_CODE) return false;
  if (fieldName !== "tripsRequired") {
    return false;
  }
  if (rawMaterialsLength === 0) return true;

  const counts = vehicleCounts(rawMaterialsLength, vehiclesPerRawMaterial);
  const info = materialIndexForFlatRow(rowIndex, counts);
  return info ? info.vehiclesForMaterial === 1 : true;
}

function fuelRowCountForValidation(
  sectionRows: Record<string, number>,
  rawMaterialsLength: number,
  fCounts: number[]
): number {
  if (rawMaterialsLength > 0) {
    return Math.max(1, totalFlatRows(fCounts));
  }
  return Math.max(1, sectionRows[A2_FUEL_SECTION_CODE] ?? 1);
}

/** Embodied energy CMS fields use A2_EMBODIED_ENERGY but values live on fuel row keys. */
function validateA2EmbodiedEnergyFields({
  sections,
  sectionRows,
  values,
  rawMaterialsLength,
  fCounts,
}: {
  sections: FormSubSection[];
  sectionRows: Record<string, number>;
  values: Record<string, string>;
  rawMaterialsLength: number;
  fCounts: number[];
}): ContributorFormErrors {
  const errors: ContributorFormErrors = {};
  const embodiedSection = sections.find(
    (s) => (s.code || s.name || String(s.id)) === A2_EMBODIED_ENERGY_CODE
  );
  if (!embodiedSection) return errors;

  const energyFields = embodiedSection.fields.filter((f) =>
    A2_EMBODIED_ENERGY_FIELD_NAMES.has(f.name)
  );
  if (energyFields.length === 0) return errors;

  const rowCount = fuelRowCountForValidation(
    sectionRows,
    rawMaterialsLength,
    fCounts
  );

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
    if (!isA2FuelRowActive(values, rowIndex)) continue;

    for (const field of energyFields) {
      if (!field.isEnabled || !field.isMandatory) continue;
      if (isA2FieldDisabled(field)) continue;
      if (shouldSkipA2UnitField(field, A2_FUEL_SECTION_CODE, rowIndex, values)) {
        continue;
      }

      const key = buildSectionFieldKey(
        field.name,
        A2_FUEL_SECTION_CODE,
        rowIndex
      );
      if (!fieldValuePresent(field, values, key) && !errors[key]) {
        errors[key] = `${fieldLabel(field)} is required.`;
      }
    }
  }

  return errors;
}

/** Exported function — validate A2Form. */
export function validateA2Form({
  sections,
  sectionRows,
  values,
  rawMaterialsLength,
  vehiclesPerRawMaterial,
  fuelsPerRawMaterial,
  formData = null,
  fuelDensityCatalog = [],
  calorificValuesCatalog = [],
}: {
  sections: FormSubSection[];
  sectionRows: Record<string, number>;
  values: Record<string, string>;
  rawMaterialsLength: number;
  vehiclesPerRawMaterial: number[];
  fuelsPerRawMaterial: number[];
  formData?: FormDataResponse | null;
  fuelDensityCatalog?: FuelDensityApiRow[];
  calorificValuesCatalog?: CalorificValuePublic[];
}): ContributorFormErrors {
  const vCounts = vehicleCounts(rawMaterialsLength, vehiclesPerRawMaterial);
  const fCounts = fuelCounts(rawMaterialsLength, fuelsPerRawMaterial);

  const errors = validateContributorSectionRows({
    sections,
    sectionRows,
    values,
    getRowCount: (sectionCode, defaultRowCount) => {
      if (sectionCode === A2_VEHICLE_CODE && rawMaterialsLength > 0) {
        return Math.max(1, totalFlatRows(vCounts));
      }
      if (sectionCode === A2_FUEL_SECTION_CODE && rawMaterialsLength > 0) {
        return Math.max(1, totalFlatRows(fCounts));
      }
      return defaultRowCount;
    },
    shouldSkipField: (field, sectionCode, rowIndex) => {
      if (sectionCode === A2_EMBODIED_ENERGY_CODE) return true;
      if (A2_NON_UI_FIELD_NAMES.has(field.name)) return true;
      if (!field.isEnabled) return true;
      if (field.type === "RAW_MATERIALS") return true;
      if (isA2FieldDisabled(field)) return true;
      if (
        sectionCode === A2_FUEL_SECTION_CODE &&
        A2_EMBODIED_ENERGY_FIELD_NAMES.has(field.name)
      ) {
        return true;
      }
      if (
        sectionCode === A2_FUEL_SECTION_CODE &&
        !isA2FuelRowActive(values, rowIndex)
      ) {
        return true;
      }
      if (
        shouldSkipFuelDensityMandatoryValidation({
          field,
          sectionCode,
          rowIndex,
          values,
          formData,
          fuelDensityCatalog,
          fuelMixFieldName: A2_FUEL_MIX_FIELD,
          fuelMixSectionCode: A2_FUEL_SECTION_CODE,
        })
      ) {
        return true;
      }
      if (
        shouldSkipCalorificValueMandatoryValidation({
          field,
          sectionCode,
          rowIndex,
          values,
          formData,
          calorificValuesCatalog,
          fuelMixFieldName: A2_FUEL_MIX_FIELD,
          fuelMixSectionCode: A2_FUEL_SECTION_CODE,
        })
      ) {
        return true;
      }
      if (shouldSkipA2UnitField(field, sectionCode, rowIndex, values)) {
        return true;
      }
      if (
        isA2TripsCalculated(
          field.name,
          sectionCode,
          rowIndex,
          rawMaterialsLength,
          vehiclesPerRawMaterial
        )
      ) {
        return true;
      }
      return false;
    },
    hasFieldValue: (field, key, fieldValues) =>
      fieldValuePresent(field, fieldValues, key),
  });

  const embodiedEnergyErrors = validateA2EmbodiedEnergyFields({
    sections,
    sectionRows,
    values,
    rawMaterialsLength,
    fCounts,
  });

  return { ...errors, ...embodiedEnergyErrors };
}
